/**
 * Connected apps — the OAuth grants a user has given to MCP connectors.
 * Backed by GET/DELETE /api/auth/connected-apps.
 *
 * Deliberately separate from session.service.ts even though both read `credentials` rows on
 * the backend. A connector is not a device: it has a client name and a scope, and no IP or
 * "this device" to report. Sharing a service would mean one response type carrying two
 * disjoint sets of optional fields.
 *
 * No CSRF handling here: api.ts's request interceptor already attaches X-CSRF-Token to every
 * POST/PUT/PATCH/DELETE.
 */
import apiClient from './api';
import type { ApiResponse } from '../types';

export interface ConnectedAppView {
  id: string;
  clientId: string;
  /** The name the client registered. Falls back server-side, so this is rarely null. */
  name: string | null;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export const connectedAppService = {
  async list(): Promise<ConnectedAppView[]> {
    const res = await apiClient.get<ApiResponse<{ apps: ConnectedAppView[] }>>(
      '/auth/connected-apps'
    );
    return res.data.data?.apps ?? [];
  },

  /**
   * Disconnects the app entirely — the backend revokes the refresh grant AND every access
   * token issued from it, so the connector stops working immediately rather than at the end
   * of the current token's hour.
   */
  async revoke(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<null>>(`/auth/connected-apps/${id}`);
  },
};
